import { useContext, useMemo } from 'react';
import { ServiceContext } from 'providers/ServiceProvider';
import { RightsContext } from 'providers/RightsContext';
import { useApplicationFeature } from 'hooks';
import { DEALER_BRANDING, ANAF_INTEGRATION } from 'constants/applicationFeatureCodes';
import { Insurance, Origination } from 'constants/contexts';
import { menuCategories } from 'constants/menuCategories';
import * as rightsCodes from 'constants/rightsCodes';
import List from '@mui/icons-material/List';
import Income from '@mui/icons-material/PlaylistAddCheck';
import Security from '@mui/icons-material/Security';
import SyncIcon from '@mui/icons-material/Sync';
import Font from '@mui/icons-material/HowToReg';
import ViewList from '@mui/icons-material/ViewList';
import DvrIcon from '@mui/icons-material/Dvr';
import Indicators from '@mui/icons-material/ShowChart';
import Sync from '@mui/icons-material/Sync';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import SchemaIcon from '@mui/icons-material/Schema';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';

const menuItems = [
  {
    icon: List,
    text: 'Dashboard.TextResource.Text',
    path: '/textResource',
    name: 'TextResource',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.TextResource.Footer',
    iconColor: 'error',
    permissions: [rightsCodes.ADMIN_TEXT_RESOURCES],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.BehaviorParamTypes.Text',
    path: '/initializationParams',
    name: 'BehaviorParamTypes',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.BehaviorParamTypes.Footer',
    iconColor: 'error',
    permissions: [rightsCodes.ADMIN_INITIALIZATION_PARAMETERS],
    contexts: [Origination, Insurance]
  },
  {
    icon: SyncIcon,
    text: 'Dashboard.AssetHierarchyImport.Text',
    path: '/assetHierarchy',
    name: 'ImportHierarchy',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.AssetHierarchyImport.Footer',
    iconColor: 'primary',
    permissions: [rightsCodes.ADMIN_ASSET_HIERARCHY],
    contexts: [Origination]
  },
  {
    icon: Security,
    text: 'Dashboard.Security.Text',
    path: '/security',
    name: 'Security',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.Security.Footer',
    iconColor: 'error',
    permissions: [rightsCodes.ADMIN_SECURITY],
    contexts: [Origination, Insurance]
  },
  {
    icon: ViewList,
    text: 'Dashboard.CancelOffers.Text',
    path: '/cancelOffers',
    name: 'CancelOffers',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.CancelOffers.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_CANCEL_OFFERS],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.Branding.Text',
    path: '/branding',
    name: 'Branding',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.Branding.Footer',
    iconColor: 'primary',
    feature: DEALER_BRANDING,
    permissions: [rightsCodes.ADMIN_BRANDING],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.DocumentTypes.Text',
    path: '/documentTypes',
    name: 'DocumentTypes',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.DocumentTypes.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_DOCUMENT_TYPES],
    contexts: [Origination, Insurance]
  },
  {
    icon: List,
    text: 'Dashboard.AutomaticStateTransitions.Text',
    path: '/automaticStateTransitions',
    name: 'AutomaticStateTransitions',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.AutomaticStateTransitions.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_AUTOMATIC_STATE_TRANSITIONS],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.DocumentTemplates.Text',
    path: '/documentTemplates',
    name: 'DocumentTemplates',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.DocumentTemplates.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_DOCUMENT_TEMPLATES],
    contexts: [Origination, Insurance]
  },
  {
    icon: ViewList,
    text: 'Dashboard.RequiredDocuments.Text',
    path: '/requiredDocuments',
    name: 'RequiredDocuments',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.RequiredDocuments.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_REQUIRED_DOCUMENTS],
    contexts: [Origination, Insurance]
  },
  {
    icon: ViewList,
    text: 'Dashboard.Checklist.Text',
    path: '/checklist',
    name: 'Checklist',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.Checklist.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_DOCUMENTS_CHECKLIST],
    contexts: [Origination, Insurance]
  },
  {
    icon: List,
    text: 'Dashboard.DocumentClause.Text',
    path: '/documentClause',
    name: 'DocumentClause',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.DocumentClause.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_CLAUSE_DOCUMENT],
    contexts: [Origination]
  },
  {
    icon: Sync,
    text: 'Dashboard.DocumentSyncState.Text',
    path: '/documentSyncState',
    name: 'DocumentSyncState',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.DocumentSyncState.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_SYNC_DOCUMENT_STATE],
    contexts: [Origination]
  },
  {
    icon: AssignmentIcon,
    text: 'Dashboard.DocumentSignStateTransitions.Text',
    path: '/documentSignStateTransitions',
    name: 'DocumentSignStateTransitions',
    iconColor: 'success',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.DocumentSignStateTransitions.Footer',
    permissions: [rightsCodes.ADMIN_DOCUMENT_SIGN_STATE_TRANSITIONS],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.CompetenceOrders.Text',
    path: '/competenceOrders',
    name: 'CompetenceOrders',
    iconColor: 'secondary',
    category: menuCategories.APPROVALS,
    footer: 'Dashboard.CompetenceOrders.Footer',
    permissions: [rightsCodes.ADMIN_COMPETENCE_ORDERS],
    contexts: [Origination]
  },
  {
    icon: Font,
    text: 'Dashboard.ApprovalTypes.Text',
    path: '/approvalTypes',
    name: 'ApprovalTypes',
    category: menuCategories.APPROVALS,
    footer: 'Dashboard.ApprovalTypes.Footer',
    iconColor: 'secondary',
    permissions: [rightsCodes.ADMIN_APPROVAL_TYPES],
    contexts: [Origination]
  },
  {
    icon: Income,
    text: 'Dashboard.IncomeSources.Text',
    path: '/incomeSources',
    name: 'IncomeSources',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.IncomeSources.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_INCOME_SOURCES],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.Dictionaries.Text',
    path: '/dictionaries',
    name: 'Dictionaries',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.Dictionaries.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_DICTIONARIES],
    contexts: [Origination, Insurance]
  },
  {
    icon: List,
    text: 'Dashboard.Property.Text',
    path: '/properties',
    name: 'Properties',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.Property.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_PROPERTIES_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.TaxRates.Text',
    path: '/taxRates',
    name: 'TaxRates',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.TaxRates.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_TAX_RATES_READ],
    roles: ['Administrator', 'Customer'],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.Items.Text',
    path: '/items',
    name: 'Items',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.Items.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_ITEMS_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.Cities.Text',
    path: '/cities',
    name: 'Cities',
    category: menuCategories.DICTIONARIES,
    footer: 'Dashboard.Cities.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_CITIES_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.FinancialProduct.Text',
    path: '/financialProducts',
    name: 'FinancialProducts',
    category: menuCategories.FINANCIAL_PRODUCT,
    footer: 'Dashboard.FinancialProduct.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_FINANCIAL_PRODUCT_READ],
    contexts: [Origination]
  },

  {
    icon: ViewList,
    text: 'Dashboard.GenericEntityFlowFinancialProduct.Text',
    path: '/genericEntityFlowFinancialProduct',
    name: 'GenericEntityFlowFinancialProduct',
    category: menuCategories.FINANCIAL_PRODUCT,
    footer: 'Dashboard.GenericEntityFlowFinancialProduct.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_FINANCIAL_PRODUCT_READ],
    contexts: [Origination, Insurance]
  },
  {
    icon: Indicators,
    text: 'Dashboard.FinancialIndicators.Text',
    path: '/financialIndicators',
    name: 'FinancialIndicators',
    category: menuCategories.SCORING,
    footer: 'Dashboard.FinancialIndicators.Footer',
    iconColor: 'rose',
    permissions: [rightsCodes.ADMIN_SCORING],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.EngineAttributes.Text',
    path: '/engineAttributes',
    name: 'EngineAttributes',
    category: menuCategories.SCORING,
    footer: 'Dashboard.EngineAttributes.Category',
    iconColor: 'rose',
    permissions: [rightsCodes.ADMIN_SCORING],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.EngineScoringGrid.Text',
    path: '/scoringEngine',
    name: 'EngineScoring',
    category: menuCategories.SCORING,
    footer: 'Dashboard.EngineScoringGrid.Footer',
    iconColor: 'rose',
    permissions: [rightsCodes.ADMIN_SCORING],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.ScoringCategories.Text',
    path: '/scoringCategories',
    name: 'ScoringCategories',
    category: menuCategories.SCORING,
    footer: 'Dashboard.ScoringCategories.Footer',
    iconColor: 'rose',
    permissions: [rightsCodes.ADMIN_SCORING],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.Reports.Text',
    path: '/reports',
    name: 'Reports',
    category: menuCategories.REPORTS,
    footer: 'Dashboard.Reports.Footer',
    iconColor: 'primary',
    permissions: [rightsCodes.ADMIN_REPORTS],
    contexts: [Origination, Insurance]
  },
  {
    icon: ViewList,
    text: 'Dashboard.ReportsVerification.Text',
    path: '/reportsVerification',
    name: 'ReportsVerification',
    category: menuCategories.REPORTS,
    footer: 'Dashboard.ReportsVerification.Footer',
    iconColor: 'primary',
    permissions: [rightsCodes.ADMIN_VERIFICATION_REPORTS],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericEvaluation.Text',
    path: '/genericEvaluation',
    name: 'GenericEvaluation',
    category: menuCategories.FORMULAS,
    footer: 'Dashboard.GenericEvaluation.Footer',
    iconColor: 'rose',
    permissions: [rightsCodes.ADMIN_GENERIC_EVALUATION],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.Questionnaires.Text',
    path: '/questionnaires',
    name: 'Questionnaires',
    category: menuCategories.QUESTIONNAIRES,
    footer: 'Dashboard.Questionnaires.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_QUESTIONNAIRES],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.VerificationTypes.Text',
    path: '/verificationTypes',
    name: 'VerificationTypes',
    category: menuCategories.VERIFICATION_TYPES,
    footer: 'Dashboard.VerificationTypes.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_VERIFICATION_TYPES],
    contexts: [Origination, Insurance]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericEntityFlowChecklist.Text',
    path: '/genericEntityFlowChecklist',
    name: 'GenericEntityFlowChecklist',
    category: menuCategories.APPROVALS,
    footer: 'Dashboard.GenericEntityFlowChecklist.Footer',
    iconColor: 'secondary',
    permissions: [rightsCodes.ADMIN_GENERIC_ENTITY_FLOW_CHECKLIST],
    contexts: [Origination, Insurance]
  },
  // {
  //   icon: ViewList,
  //   text: 'Dashboard.GenericEntityFlowGenericMatrix.Text',
  //   path: '/genericEntityFlowGenericMatrix',
  //   name: 'GenericEntityFlowGenericMatrix',
  //   category: "",
  //   footer: 'Dashboard.GenericEntityFlowGenericMatrix.Footer',
  //   iconColor: 'warning',
  //   contexts: [Origination]
  // },
  // {
  //   icon: ViewList,
  //   text: 'Dashboard.GenericEntityFlowGenericMatrixTemplate.Text',
  //   path: '/genericEntityFlowGenericMatrixTemplate',
  //   name: 'GenericEntityFlowGenericMatrixTemplate',
  //   category: "",
  //   footer: 'Dashboard.GenericEntityFlowGenericMatrixTemplate.Footer',
  //   iconColor: 'warning',
  //   contexts: [Origination]
  // },
  // {
  //   icon: ViewList,
  //   text: 'Dashboard.GenericEntityFlowSubsidyMatrix.Text',
  //   path: '/genericEntityFlowSubsidyMatrix',
  //   name: 'GenericEntityFlowSubsidyMatrix',
  //   footer: 'Dashboard.GenericEntityFlowSubsidyMatrix.Footer',
  //   iconColor: 'warning',
  //   contexts: [Origination]
  // },
  {
    icon: ViewList,
    text: 'Dashboard.Tasks.Text',
    path: 'REACT_APP_LSNG_TASKS_URL',
    isExternal: true,
    name: 'Tasks',
    category: menuCategories.INFRASTRUCTURE_SERVICES,
    footer: 'Dashboard.Tasks.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_TASKS],
    contexts: [Origination]
  },
  {
    icon: DvrIcon,
    text: 'Dashboard.Monitoring.Text',
    path: 'REACT_APP_LSNG_MONITORING_INVALID_MESSAGES_URL',
    isExternal: true,
    name: 'Monitoring',
    category: menuCategories.INFRASTRUCTURE_SERVICES,
    footer: 'Dashboard.Monitoring.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_MONITORING],
    contexts: [Origination]
  },
  // {
  //   icon: TextFieldsIcon,
  //   text: "Dashboard.NotifierTemplates.Text",
  //   path: "REACT_APP_LSNG_NOTIFIER_TEMPLATES_URL",
  //   isExternal: true,
  //   name: "NotifierTemplates",
  //   category: menuCategories.INFRASTRUCTURE_SERVICES,
  //   footer: "Templates configuration application",
  //   iconColor: "info",
  //   contexts: [Origination]
  // },
  {
    icon: TimelineIcon,
    text: 'Dashboard.Atlas.Text',
    path: 'REACT_APP_ATLAS_URL',
    isExternal: true,
    name: 'Conductor',
    category: menuCategories.INFRASTRUCTURE_SERVICES,
    footer: 'Dashboard.Atlas.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_ATLAS],
    contexts: [Origination]
  },
  {
    icon: Security,
    text: 'Dashboard.Ice.Text',
    path: 'REACT_APP_ICE_URL',
    isExternal: true,
    name: 'ICE',
    category: menuCategories.INFRASTRUCTURE_SERVICES,
    footer: 'Dashboard.Ice.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_ICE],
    contexts: [Origination, Insurance]
  },
  {
    icon: Security,
    text: 'Dashboard.Anaf.Text',
    path: 'REACT_APP_ANAF_QUERY_CLIENT',
    isExternal: true,
    name: 'AnafQueryClient',
    category: menuCategories.INFRASTRUCTURE_SERVICES,
    footer: 'Dashboard.Anaf.Footer',
    iconColor: 'secondary',
    feature: ANAF_INTEGRATION,
    permissions: [rightsCodes.ADMIN_ANAF_QUERY_CLIENT],
    contexts: [Origination]
  },
  {
    icon: PeopleAltIcon,
    text: 'Dashboard.Users.Text',
    path: '/users',
    name: 'Users',
    category: menuCategories.USERS,
    footer: 'Dashboard.Users.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_USER_GROUPS_READ],
    contexts: [Origination]
  },
  {
    icon: GroupIcon,
    text: 'Dashboard.UserGroups.Text',
    path: '/userGroups',
    name: 'UserGroups',
    category: menuCategories.USERS,
    footer: 'Dashboard.UserGroups.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_USER_GROUPS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.AutomaticUserAllocation.Text',
    path: '/automaticUserAllocation',
    name: 'AutomaticUserAllocation',
    category: menuCategories.USERS,
    footer: 'Dashboard.AutomaticUserAllocation.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_AUTOMATIC_USER_ALLOCATION],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.PartnerType.Text',
    path: '/partnerTypes',
    name: 'PartnerTypes',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.PartnerType.Footer',
    iconColor: 'secondary',
    permissions: [rightsCodes.ADMIN_PARTNER_TYPES_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.PartnerConsent.Text',
    path: '/partnerConsents',
    name: 'PartnerConsents',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.PartnerConsent.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_CONSENTS_ON_PARTNER_TYPES],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericFilterParams.Text',
    path: '/genericFilterParams',
    name: 'GenericFilterParams',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.GenericFilterParams.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_GENERIC_FILTER_PARAMS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericFilterTemplate.Text',
    path: '/genericFilterTemplates',
    name: 'GenericFilterTemplates',
    category: menuCategories.DOCUMENTS,
    footer: 'Dashboard.GenericFilterTemplate.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_GENERIC_FILTER_TEMPLATES_READ],
    contexts: [Origination]
  },

  {
    icon: AccountBalanceIcon,
    text: 'Dashboard.ExchangeRates.Text',
    path: '/exchangeRates',
    name: 'ExchangeRates',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.ExchangeRates.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_EXCHANGE_RATES],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.LeasingInterestTypes.Text',
    path: '/leasingInterests',
    name: 'LeasingInterestTypes',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.LeasingInterestTypes.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_LEASING_INTERESTS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.LeasingInterestTypeDetail.Text',
    path: '/leasingInterestTypeDetails',
    name: 'LeasingInterestTypeDetails',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.LeasingInterestTypeDetail.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_LEASING_INTERESTS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.LeasingCommission.Text',
    path: '/leasingCommissions',
    name: 'LeasingCommissions',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.LeasingCommission.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_COMMISSIONS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.CommissionCalculationMethod.Text',
    path: '/commissionCalculationMethods',
    name: 'CommissionCalculationMethods',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.CommissionCalculationMethod.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_COMMISSION_CALCULATION_METHOD_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.LeasingExpenseType.Text',
    path: '/leasingExpenseTypes',
    name: 'LeasingExpenseType',
    category: menuCategories.FINANCIAL_INFO,
    footer: 'Dashboard.LeasingExpenseType.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_LEASING_EXPENSE_TYPES_READ],
    contexts: [Origination]
  },
  {
    icon: HomeRepairServiceIcon,
    text: 'Dashboard.LeasingServiceTypes.Text',
    path: '/leasingServiceTypes',
    name: 'LeasingServiceTypes',
    category: menuCategories.SERVICES,
    footer: 'Dashboard.LeasingServiceTypes.Footer',
    iconColor: 'error',
    permissions: [rightsCodes.ADMIN_LEASING_SERVICE_TYPES_READ],
    contexts: [Origination]
  },
  {
    icon: MergeTypeIcon,
    text: 'Dashboard.LeasingServiceTypeLinkings.Text',
    path: '/leasingServiceTypeLinkings',
    name: 'LeasingServiceTypeLinkings',
    category: menuCategories.SERVICES,
    footer: 'Dashboard.LeasingServiceTypeLinkings.Footer',
    iconColor: 'error',
    permissions: [rightsCodes.ADMIN_LEASING_SERVICE_TYPES_READ],
    contexts: [Origination]
  },
  {
    icon: Grid4x4Icon,
    text: 'Dashboard.ResidualValueGrids.Text',
    path: '/residualValueGrids',
    name: 'ResidualValueGrids',
    category: menuCategories.RESIDUAL_VALUE_GRID,
    footer: 'Dashboard.ResidualValueGrids.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_RESIDUAL_VALUE_GRIDS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericEntityFlowResidualValueGrid.Text',
    path: '/genericEntityFlowResidualValueGrid',
    name: 'GenericEntityFlowResidualValueGrid',
    category: menuCategories.RESIDUAL_VALUE_GRID,
    footer: 'Dashboard.GenericEntityFlowResidualValueGrid.Footer',
    iconColor: 'warning',
    permissions: [rightsCodes.ADMIN_RESIDUAL_VALUE_GRIDS_READ],
    contexts: [Origination]
  },
  {
    icon: SchemaIcon,
    text: 'Dashboard.LeasingIncomeSchema.Text',
    path: '/leasingIncomeSchemas',
    isExternal: false,
    name: 'LeasingIncomeSchemas',
    category: menuCategories.LEASING_INCOME_SCHEMA,
    footer: 'Dashboard.LeasingIncomeSchema.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_LEASING_INCOME_SCHEMAS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.GenericEntityFlowIncomeSchema.Text',
    path: '/genericEntityFlowIncomeSchema',
    isExternal: false,
    name: 'GenericEntityFlowIncomeSchema',
    category: menuCategories.LEASING_INCOME_SCHEMA,
    footer: 'Dashboard.GenericEntityFlowIncomeSchema.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_LEASING_INCOME_SCHEMAS_READ],
    contexts: [Origination]
  },
  {
    icon: ViewList,
    text: 'Dashboard.InsuranceConventions.Text',
    path: '/insuranceConventions',
    name: 'InsuranceConventions',
    category: menuCategories.INSURANCE,
    footer: 'Dashboard.InsuranceConventions.Footer',
    iconColor: 'info',
    permissions: [rightsCodes.ADMIN_INSURANCE_CONVENTIONS_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.NumericPrecision.Text',
    path: '/numericPrecision',
    name: 'NumericPrecision',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.NumericPrecision.Footer',
    iconColor: 'primary',
    permissions: [rightsCodes.ADMIN_FIELD_DECIMAL_PLACES_CONFIG_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.AccountStatement.Text',
    path: '/accountStatement',
    name: 'AccountStatement',
    category: menuCategories.SETTINGS,
    footer: 'Dashboard.AccountStatement.Footer',
    iconColor: 'primary',
    permissions: [rightsCodes.ADMIN_ACCOUNT_STATEMENT_READ],
    contexts: [Origination]
  },
  {
    icon: CalendarMonthIcon,
    text: 'Dashboard.ScheduleTypeCellParamTemplate.Text',
    path: '/scheduleTypeCellParamTemplates',
    name: 'ScheduleTypeCellParamTemplate',
    category: menuCategories.SCHEDULES,
    footer: 'Dashboard.ScheduleTypeCellParamTemplate.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_SCHEDULES_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.ContentTypes.Text',
    path: '/contentTypes',
    name: 'ContentTypes',
    category: menuCategories.DMS,
    footer: 'Dashboard.ContentTypes.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_DMS_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.Metadata.Text',
    path: '/metadata',
    name: 'Metadata',
    category: menuCategories.DMS,
    footer: 'Dashboard.Metadata.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_DMS_READ],
    contexts: [Origination]
  },
  {
    icon: List,
    text: 'Dashboard.NonWorkingDays.Text',
    path: '/nonWorkingDays',
    name: 'NonWorkingDays',
    category: menuCategories.NON_WORKING_DAYS,
    footer: 'Dashboard.NonWorkingDays.Footer',
    iconColor: 'success',
    permissions: [rightsCodes.ADMIN_NON_WORKING_DAYS_READ],
    contexts: [Origination]
  }
];

const useDashboardMenu = () => {
  const [activeService] = useContext(ServiceContext);
  const { rights, loaded: rightsLoaded } = useContext(RightsContext);

  const { applicationFeatureFlag: dealerBrandingEnabled, loading: dealerBrandingFeatureLoading } = useApplicationFeature(DEALER_BRANDING);
  const { applicationFeatureFlag: anafIntegrationEnabled, loading: anafIntegrationFeatureLoading } =
    useApplicationFeature(ANAF_INTEGRATION);

  const applicationFeatures = useMemo(
    () => ({
      DEALER_BRANDING: dealerBrandingEnabled,
      ANAF_INTEGRATION: anafIntegrationEnabled
    }),
    [dealerBrandingEnabled, anafIntegrationEnabled]
  );

  const allowedMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      if (item.feature && !applicationFeatures[item.feature]) return false;
      if (item.permissions) {
        return item.permissions.every(permission => rights.rights?.includes(permission));
      }
      return true;
    });
  }, [rights, applicationFeatures]);

  const result = useMemo(() => {
    if (!allowedMenuItems || !activeService) return [];
    return allowedMenuItems
      .filter(x => x.contexts && x.contexts.some(y => y.serviceRoute === activeService?.route))
      .map(x => {
        return { ...x, path: !x.isExternal && activeService != null ? `/${activeService.route}${x.path}` : x.path };
      });
  }, [allowedMenuItems, activeService]);

  const loading = useMemo(
    () => !rightsLoaded || dealerBrandingFeatureLoading || anafIntegrationFeatureLoading,
    [rightsLoaded, dealerBrandingFeatureLoading, anafIntegrationFeatureLoading]
  );

  return { menuItems: result, loading };
};

const useDashboardMenuItem = pathName => {
  const [activeService] = useContext(ServiceContext);
  const menuItem = useMemo(() => {
    const serviceRoute = activeService?.route;
    return menuItems.find(
      x => pathName.toLowerCase().endsWith(x.path.toLowerCase()) && x.contexts.some(x => x.serviceRoute === serviceRoute)
    );
  }, [pathName, activeService]);

  return menuItem;
};

const useServiceRoute = path => {
  const firstIndex = path.indexOf('/');
  const secondIndex = path.indexOf('/', firstIndex + 1);
  const lastIndex = secondIndex < 0 ? path.length : path.indexOf('/', firstIndex + 1);

  const basePath = path.substring(firstIndex, lastIndex);
  const menu = menuItems.find(x => x.path === basePath);

  if (menu && menu.contexts) {
    const concatServices = menu.contexts
      .map(x => {
        return x.serviceRoute;
      })
      .join('|');

    const serviceContextString = '/:serviceContext';
    path = `${serviceContextString}(:?${concatServices})${path}`;
  }

  return { path, menuId: menu?.name };
};

export { useDashboardMenu, useDashboardMenuItem, useServiceRoute };
